import { useAuth } from '@/domain/context/auth';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { ALL_AUDIENCE } from '@/domain/types/audience';
import { CollectionBase } from '@/domain/types/collections';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import useGetDocument from '@/domain/use-cases/get-document';
import {
  buildReleaseToAllPayload,
  isReleasingToAll,
} from '@/domain/use-cases/releases/release-to-all';
import AlertMessage from '@/presentation/components/alert-message';
import CommentsDetailDocument from '@/presentation/components/detail-document/comments';
import HeaderDetailDocument from '@/presentation/components/detail-document/header';
import StatsDetailDocument from '@/presentation/components/detail-document/stats';
import FormAddDocument from '@/presentation/components/form-add-document';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const ReleaseDetailsPage = () => {
  const [isAlertMessageOpen, setIsAlertMessageOpen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logUpdate, logDelete, logReleaseToAll } = useAdminLogger();

  const {
    data,
    error,
    isLoading,
    updateDocument,
    deleteDocument,
    deleteComment,
    comments,
  } = useGetDocument<CollectionBase>(DOCUMENTS_DB.RELEASES, id);

  const handleDelete = async () => {
    try {
      await deleteDocument();

      // Log the action
      await logDelete(DOCUMENTS_DB.RELEASES, id || '', data?.title);

      toast.success('Última hora eliminada correctamente', {
        theme: 'colored',
      });
      navigate('/dashboard/releases');
    } catch (error) {
      console.log(error);
      toast.error('Error al eliminar última hora', { theme: 'colored' });
    }
  };

  const handleSave = async (updateData: CollectionBase) => {
    try {
      // FormAddDocument entrega audienceIds YA resuelto (['ALL'] o la lista
      // real). isReleasingToAll/buildReleaseToAllPayload esperan la selección
      // cruda (vacía = pública), así que se invierte aquí la resolución.
      const selectedAudiences = updateData.audienceIds?.includes(ALL_AUDIENCE)
        ? []
        : (updateData.audienceIds ?? []);

      const releasing = isReleasingToAll(data?.audienceIds, selectedAudiences);
      if (releasing) {
        const confirmed = window.confirm(
          'Esta publicación pasará a ser visible para TODOS los usuarios de INTER y se enviará una notificación push general. ¿Confirmas la liberación?',
        );
        if (!confirmed) return;
      }

      const updatedData = { ...data, ...updateData };

      // CRÍTICO: el gate es `releasing`, no solo `user`. buildReleaseToAllPayload
      // estampa releasedToAllAt/releasedToAllBy cada vez que selectedAudiences
      // está vacío — y eso es cierto tanto en la liberación real (restringida →
      // pública) como en CUALQUIER edición posterior de una publicación que YA
      // era pública (título, imagen, lo que sea). Sin el `releasing &&` aquí,
      // cada edición trivial de una publicación general reescribiría la fecha y
      // el autor de la liberación con los del último editor — exactamente lo
      // que la trazabilidad que pidió el cliente debe evitar.
      if (releasing && user) {
        const payload = buildReleaseToAllPayload(selectedAudiences, {
          id: user.id,
          name: `${user.name} ${user.lastname}`,
          email: user.email,
        });
        // Un único updateDoc: ver el comentario en buildReleaseToAllPayload.
        await updateDocument({ ...updatedData, ...payload });
      } else {
        // No es una liberación (o no hay user, caso defensivo): se escribe
        // updatedData tal cual, que ya trae el audienceIds correcto vía
        // updateData. Si la publicación ya tenía releasedToAllAt de una
        // liberación anterior, updatedData lo conserva sin tocarlo (viene de
        // `...data`), preservando la fecha original.
        await updateDocument(updatedData);
      }

      if (releasing) {
        await logReleaseToAll(
          DOCUMENTS_DB.RELEASES,
          id || '',
          updatedData.title,
          data?.audienceIds,
        );
      } else {
        await logUpdate(
          DOCUMENTS_DB.RELEASES,
          id || '',
          updatedData.title,
          updateData,
        );
      }

      toast.success('Última hora actualizada correctamente', {
        theme: 'colored',
      });
    } catch (error) {
      console.log(error);
      toast.error('Error al actualizar última hora', { theme: 'colored' });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  if (error) return <div>Error: {error}</div>;

  if (!data) return <div>No data</div>;

  return (
    <div className="p-4 flex flex-row md:flex-col gap-8">
      <HeaderDetailDocument
        title={data.title}
        handleDelete={() => setIsAlertMessageOpen(true)}
      />

      {data?.releasedToAllAt &&
        data?.releasedToAllBy &&
        data?.audienceIds?.includes(ALL_AUDIENCE) && (
        <p className="text-sm text-gray-600">
          Liberada a todos por {data.releasedToAllBy.name} el{' '}
          {new Date(data.releasedToAllAt.seconds * 1000).toLocaleString('es-CO')}
        </p>
      )}

      <StatsDetailDocument data={data} />

      <FormAddDocument onSave={handleSave} document={data} showAudienceSelector />

      <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
        <CommentsDetailDocument data={comments} deleteComment={deleteComment} />
      </div>

      <AlertMessage
        title="Eliminar Última Hora"
        message="¿Estás seguro de querer eliminar este contenido?"
        onConfirm={handleDelete}
        onCancel={() => setIsAlertMessageOpen(false)}
        isOpen={isAlertMessageOpen}
      />
    </div>
  );
};

export default ReleaseDetailsPage;
